import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  FaSearch,
  FaCheckCircle,
  FaIdCard,
  FaFingerprint,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaShieldAlt,
  FaWallet,
  FaHourglassHalf,
  FaClipboardList,
  FaHeadset,
  FaMoneyBillWave,
  FaFemale,
  FaChild,
  FaFireAlt,
  FaUserCircle,
} from "react-icons/fa";
import "../../Styles/BeneficiaryLookup.css";
import { beneficiariesAPI, schemesAPI } from "../../api/index";

const schemeIconMap = {
  cmrf: <FaMoneyBillWave />,
  aadabidda: <FaFemale />,
  thalliki: <FaChild />,
  deepam: <FaFireAlt />,
  annadata: <FaClipboardList />,
  yuvagalam: <FaClipboardList />,
  mahashakthi: <FaClipboardList />,
};

/* ---------------- helpers ---------------- */

const extractList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || d?.data || [];
};

// first non-empty value among several possible field names
const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fallback;
};

// Picks the icon from the scheme name / slug
const getIconKey = (text = "") => {
  const n = String(text).toLowerCase();
  if (n.includes("relief") || n.includes("cmrf")) return "cmrf";
  if (n.includes("aadabidda")) return "aadabidda";
  if (n.includes("thalliki")) return "thalliki";
  if (n.includes("deepam")) return "deepam";
  if (n.includes("annadata")) return "annadata";
  if (n.includes("yuvagalam")) return "yuvagalam";
  if (n.includes("maha")) return "mahashakthi";
  return "";
};

// "2026-09-16" -> "16 Sep 2026"
const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// "approved" -> "Approved"
const formatStatus = (value) => {
  const s = String(value || "Pending").replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

// The lookup may return a plain list, or an object holding a list of records
const extractRows = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  const listKey = [
    "schemes", "beneficiaries", "records", "items", "results", "benefits", "data",
  ].find((k) => Array.isArray(data[k]));
  if (listKey) return data[listKey];
  return Object.keys(data).length ? [data] : [];
};

// Builds { [scheme_id]: { name, slug } } from the schemes master list
const buildSchemesMap = (list) => {
  const map = {};
  list.forEach((s) => {
    map[s.id] = {
      name: pick(s, ["name", "scheme_name", "title", "scheme_title"], ""),
      slug: pick(s, ["slug", "code", "scheme_code"], ""),
    };
  });
  return map;
};

// One lookup row (a scheme the person availed) -> the shape the table uses
const normalizeRecord = (row, schemesMap) => {
  const scheme = schemesMap[row.scheme_id];
  const schemeObj = typeof row.scheme === "object" && row.scheme ? row.scheme : null;

  const schemeName =
    pick(row, ["scheme_name", "scheme_title"]) ||
    schemeObj?.name ||
    (typeof row.scheme === "string" ? row.scheme : "") ||
    scheme?.name ||
    (row.scheme_id != null ? `Scheme #${row.scheme_id}` : "Scheme");

  const rawDate = pick(row, [
    "availed_date", "approved_date", "disbursed_date", "date", "application_date", "created_at",
  ]);

  return {
    id: row.id,
    scheme: schemeName,
    icon: getIconKey(`${schemeName} ${scheme?.slug || ""}`),
    amount:
      Number(
        pick(row, ["amount", "amount_received", "benefit_amount", "approved_amount", "sanctioned_amount"], 0)
      ) || 0, // "3000.00" -> 3000
    rawDate,
    date: formatDate(rawDate),
    status: formatStatus(row.status),
    // candidate details, only if the lookup row happens to include them
    name: pick(row, ["beneficiary_name", "name", "voter_name", "full_name"]),
    voterId: pick(row, ["epic_no", "voter_id"]),
    aadhaar: pick(row, ["aadhaar_masked", "aadhaar", "aadhaar_number"]),
    village: pick(row, ["village_name", "village"]),
    mandal: pick(row, ["mandal_name", "mandal"]),
  };
};

// GET /beneficiaries record -> candidate profile
const normalizeProfile = (item) => ({
  name: pick(item, ["beneficiary_name", "name"]),
  voterId: pick(item, ["epic_no", "voter_id"]),
  aadhaar: pick(item, ["aadhaar_masked", "aadhaar", "aadhaar_number"]),
  village: pick(item, ["village_name", "village"]),
  mandal: pick(item, ["mandal_name", "mandal"]),
});

// earliest date among the records -> "Beneficiary Since"
const earliestDate = (rows) => {
  const times = rows
    .map((r) => new Date(r.rawDate).getTime())
    .filter((t) => !Number.isNaN(t));
  return times.length ? formatDate(new Date(Math.min(...times))) : "—";
};

/* ---------------- component ---------------- */

const AdminBeneficiaryLookup = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // scheme rows
  const [profile, setProfile] = useState(null); // candidate details
  const [searchedEpic, setSearchedEpic] = useState("");
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const schemesMapRef = useRef({}); // scheme_id -> { name, slug }

  // Loads the schemes master list (used when a row only has scheme_id)
  const loadSchemes = async () => {
    try {
      const res = await schemesAPI.getAll({ page_size: 100 });
      schemesMapRef.current = buildSchemesMap(extractList(res));
    } catch (err) {
      // non-fatal: rows fall back to the scheme name from the row / "Scheme #<id>"
    }
    return schemesMapRef.current;
  };

  useEffect(() => {
    loadSchemes();
  }, []);

  // Finds the candidate's record in GET /beneficiaries (it has name, village, mandal...).
  // The filter parameter name isn't known, so try "search" then "epic_no", and
  // only accept a record whose epic_no exactly matches what was searched.
  const fetchProfile = async (epic) => {
    const attempts = [{ search: epic }, { epic_no: epic }];
    for (const extra of attempts) {
      try {
        const res = await beneficiariesAPI.getAll({ page: 1, page_size: 50, ...extra });
        const match = extractList(res).find(
          (r) => String(r.epic_no || "").toUpperCase() === epic
        );
        if (match) return normalizeProfile(match);
      } catch (err) {
        // try the next parameter name
      }
    }
    return null;
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const epic = query.trim().toUpperCase();
    if (!epic) return;

    setLoading(true);
    try {
      const schemesMap = Object.keys(schemesMapRef.current).length
        ? schemesMapRef.current
        : await loadSchemes();

      // scheme rows + candidate details, fetched at the same time
      const [lookupResult, profileResult] = await Promise.allSettled([
        beneficiariesAPI.lookup(epic),
        fetchProfile(epic),
      ]);

      if (lookupResult.status === "rejected") {
        const err = lookupResult.reason;
        // 404 just means "not found" -> show the empty state, no error toast
        if (err?.response?.status !== 404) {
          const detail = err?.response?.data?.detail;
          toast.error(
            typeof detail === "string" ? detail : "Could not fetch beneficiary details"
          );
        }
        setResults([]);
        setProfile(null);
      } else {
        setResults(
          extractRows(lookupResult.value.data).map((row) => normalizeRecord(row, schemesMap))
        );
        setProfile(profileResult.status === "fulfilled" ? profileResult.value : null);
      }
      setSearchedEpic(epic);
    } finally {
      setSearched(true);
      setLoading(false);
    }
  };

  const totalSchemes = results ? results.length : 0;
  const totalAmount = results ? results.reduce((sum, r) => sum + r.amount, 0) : 0;
  const pendingCount = results
    ? results.filter((r) => r.status.toLowerCase() === "pending").length
    : 0;

  // Profile card: prefer the GET /beneficiaries details, then anything on the lookup row
  const firstRow = results && results.length > 0 ? results[0] : null;
  const person = firstRow
    ? {
        name: profile?.name || firstRow.name,
        voterId: profile?.voterId || firstRow.voterId || searchedEpic,
        aadhaar: profile?.aadhaar || firstRow.aadhaar,
        village: profile?.village || firstRow.village,
        mandal: profile?.mandal || firstRow.mandal,
      }
    : null;

  const beneficiarySince = firstRow ? earliestDate(results) : "";

  const locationText = person
    ? [
        person.village ? `${person.village} Village` : "",
        person.mandal ? `${person.mandal} Mandal` : "",
      ]
        .filter(Boolean)
        .join(", ") || "—"
    : "";

  return (
    <div className="lookup-page">
      {/* Header */}
      <div className="lookup-hero">
        <div className="lookup-hero-overlay"></div>
        <div className="lookup-hero-content">
          <h1>
            Beneficiary <span className="lookup-hero-highlight">360°</span> Lookup
          </h1>
          <p>Search by Voter ID (EPIC No) to view all schemes availed</p>

          <form className="lookup-search-bar" onSubmit={handleSearch}>
            <FaSearch className="lookup-search-icon" />
            <input
              type="text"
              placeholder="Enter Voter ID (EPIC No)..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button type="submit" className="lookup-search-btn" disabled={loading}>
              <FaSearch />
              {loading ? "Searching..." : "Search"}
            </button>
          </form>

          <p className="lookup-search-hint">
            Search using <strong>Voter ID</strong> (eg. APR1001231)
          </p>
        </div>
      </div>

      <div className="lookup-container">
        {/* IDLE STATE — before the very first search */}
        {!searched && (
          <div className="lookup-idle-state">
            <FaUserCircle className="lookup-idle-icon" />
            <h3>No Beneficiary Selected</h3>
            <p>Enter a Voter ID above and click Search to view scheme details.</p>
          </div>
        )}

        {/* NO MATCH FOUND */}
        {searched && results && results.length === 0 && (
          <div className="lookup-empty-state">
            <p>No records found for this Voter ID.</p>
          </div>
        )}

        {/* RESULTS */}
        {searched && results && results.length > 0 && (
          <>
            {/* Profile Card */}
            <div className="lookup-profile-card">
              <div className="lookup-profile-left">
                <div className="lookup-profile-avatar">
                  {(person.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="lookup-profile-info">
                  <div className="lookup-profile-name-row">
                    <h3>{person.name || "Unknown"}</h3>
                    <span className="lookup-verified-badge">
                      <FaCheckCircle /> Verified
                    </span>
                  </div>

                  <div className="lookup-profile-meta">
                    <span>
                      <FaIdCard className="lookup-meta-icon" />
                      Voter ID
                      <strong>{person.voterId || "—"}</strong>
                    </span>
                    <span>
                      <FaFingerprint className="lookup-meta-icon" />
                      Aadhaar Number
                      <strong>{person.aadhaar || "—"}</strong>
                    </span>
                    <span>
                      <FaMapMarkerAlt className="lookup-meta-icon" />
                      Location
                      <strong>{locationText}</strong>
                    </span>
                  </div>
                </div>
              </div>

              <div className="lookup-profile-right">
                <div className="lookup-mini-stat">
                  <FaCalendarAlt className="lookup-mini-icon" />
                  <div>
                    <p>Beneficiary Since</p>
                    <strong>{beneficiarySince}</strong>
                  </div>
                </div>
                <div className="lookup-mini-stat">
                  <FaShieldAlt className="lookup-mini-icon" />
                  <div>
                    <p>Total Schemes Availed</p>
                    <strong>{totalSchemes}</strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Stat Cards */}
            <div className="lookup-stats-grid">
              <div className="lookup-stat-card lookup-stat-blue">
                <div className="lookup-stat-icon-box lookup-stat-icon-blue">
                  <FaClipboardList />
                </div>
                <div>
                  <p className="lookup-stat-label">Total Schemes Availed</p>
                  <h3 className="lookup-stat-value">{totalSchemes}</h3>
                  <span className="lookup-stat-sub">Across All Schemes</span>
                </div>
              </div>

              <div className="lookup-stat-card lookup-stat-green">
                <div className="lookup-stat-icon-box lookup-stat-icon-green">
                  <FaWallet />
                </div>
                <div>
                  <p className="lookup-stat-label">Total Benefit Received</p>
                  <h3 className="lookup-stat-value">
                    ₹{totalAmount.toLocaleString("en-IN")}
                  </h3>
                  <span className="lookup-stat-sub">Across All Schemes</span>
                </div>
              </div>

              <div className="lookup-stat-card lookup-stat-orange">
                <div className="lookup-stat-icon-box lookup-stat-icon-orange">
                  <FaHourglassHalf />
                </div>
                <div>
                  <p className="lookup-stat-label">Pending Requests</p>
                  <h3 className="lookup-stat-value">{pendingCount}</h3>
                  <span className="lookup-stat-sub">Awaiting Action</span>
                </div>
              </div>
            </div>

            {/* Scheme Breakdown Table */}
            <div className="lookup-table-card">
              <h3 className="lookup-section-title">
                <FaClipboardList className="lookup-section-icon" />
                Scheme-wise Breakdown
              </h3>

              <div className="lookup-table-wrapper">
                <table className="lookup-table">
                  <thead>
                    <tr>
                      <th>SNO</th>
                      <th>Scheme Name</th>
                      <th>Amount Received</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row, index) => (
                      <tr key={row.id ?? `${row.scheme}-${index}`}>
                        <td>{index + 1}</td>
                        <td className="lookup-scheme-name">
                          <span className="lookup-scheme-icon">
                            {schemeIconMap[row.icon] || <FaClipboardList />}
                          </span>
                          {row.scheme}
                        </td>
                        <td
                          className={
                            row.amount > 0 ? "lookup-amount" : "lookup-amount-inkind"
                          }
                        >
                          {row.amount > 0
                            ? `₹${row.amount.toLocaleString("en-IN")}`
                            : "In-kind Benefit"}
                        </td>
                        <td>
                          <FaCalendarAlt className="lookup-date-icon" />
                          {row.date}
                        </td>
                        <td>
                          <span
                            className={`lookup-status ${
                              row.status === "Approved"
                                ? "lookup-approved"
                                : "lookup-pending"
                            }`}
                          >
                            <FaCheckCircle className="lookup-status-icon" />
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Footer bar - always visible */}
        <div className="lookup-footer-bar">
          <div className="lookup-footer-left">
            <FaShieldAlt className="lookup-footer-icon" />
            <p>
              This information is provided by the Government of Andhra Pradesh.
              For any queries, please contact your nearest MeeSeva / Sachivalayam.
            </p>
          </div>
          <div className="lookup-footer-right">
            <FaHeadset className="lookup-footer-headset" />
            <div>
              <p>CM Helpline</p>
              <strong>1902</strong>
              <span>24x7 Assistance</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBeneficiaryLookup;